import { type FC, Fragment, type ReactNode } from "react";

import {
  Card,
  CardRowDetail,
  CardRowDetailColumn,
  CardRowDetailLine,
  CardRowSimple,
  type GridCard,
  HStack,
  InfoToggletip,
} from "widgets";

import { type IMaxHitRange, getMaxHitRange } from "../../../app-utils/blast.js";
import { type BlastResult, type Protein } from "../../../graphql.js";
import HitGraphics from "../../blast-result/HitGraphics.js";
import { GenomeTile, ProteinTile } from "../../core/tiles/index.js";

export interface IBestHitCardProps extends GridCard {
  protein: Protein;
}

const BestHitCard: FC<IBestHitCardProps> = (props) => {
  if (!props.protein || !props.protein.bestBlastResults || props.protein.bestBlastResults.length === 0) {
    return null;
  }

  const maxHitRange: IMaxHitRange = getMaxHitRange(props.protein.length, props.protein.bestBlastResults);

  const content = props.protein.bestBlastResults.map((bestHit: BlastResult) => {
    const qResidues = bestHit.queryStop - bestHit.queryStart + 1;
    const tResidues = bestHit.targetStop - bestHit.targetStart + 1;
    const queryCoverage = ((100 * qResidues) / props.protein.length).toFixed(1);
    const targetCoverage = ((100 * tResidues) / bestHit.protein.length).toFixed(1);

    const queryTooltipText: string =
      "The alignment covers " +
      queryCoverage +
      "% (" +
      qResidues +
      " residues) of " +
      props.protein.sequenceId +
      "'s sequence.";
    const targetTooltipText: string =
      "The alignment covers " +
      targetCoverage +
      "% (" +
      tResidues +
      " residues) of " +
      bestHit.protein.sequenceId +
      "'s sequence.";
    const alnTooltipText: string =
      "The alignment's score is " +
      bestHit.score +
      " bits with " +
      bestHit.positives +
      " % positively scoring residues and " +
      bestHit.identicals +
      " % identical residues.";
    const pValueTooltipText: string = "The P-Value of the best hit is " + bestHit.pValue;

    return (
      <Fragment key={"ref-card-elem-" + bestHit.id}>
        <CardRowDetail title={bestHit.protein.sequenceId} style={{ fontSize: 12 }}>
          <CardRowDetailColumn>
            <tr>
              <td colSpan={2}>
                <HitGraphics
                  maxHitRange={maxHitRange}
                  queryProteinLength={props.protein.length}
                  blastResult={bestHit}
                  width={300}
                />
              </td>
            </tr>
            <BestHitCardRowValue label={"Query Coverage:"} labelTooltip={queryTooltipText}>
              {queryCoverage}%
            </BestHitCardRowValue>
            <BestHitCardRowValue label={"Target Coverage:"} labelTooltip={targetTooltipText}>
              {targetCoverage}%
            </BestHitCardRowValue>
            <BestHitCardRowValue label={"Score:"} labelTooltip={alnTooltipText} wrap>
              {bestHit.score} ({bestHit.positives}% positives | {bestHit.identicals}% identicals)
            </BestHitCardRowValue>
            <BestHitCardRowValue label={"P-Value:"} labelTooltip={pValueTooltipText}>
              {bestHit.pValue}
            </BestHitCardRowValue>
          </CardRowDetailColumn>
        </CardRowDetail>
        <CardRowSimple>
          <ProteinTile protein={bestHit.protein} />
          <GenomeTile genome={bestHit.protein?.genome} />
        </CardRowSimple>
      </Fragment>
    );
  });

  return (
    <Card
      shrinkable={props.protein.bestBlastResults.length > 3}
      title={"Best Hits"}
      subtitle={"Lists the 5 best BLAST hits of this amino acid sequence and all the proteins of another genome."}
    >
      {content}
    </Card>
  );
};

interface BestHitCardRowValueProps {
  label: string;
  labelTooltip: string;
  wrap?: boolean;
  children: ReactNode;
}

const BestHitCardRowValue: FC<BestHitCardRowValueProps> = (props) => {
  return (
    <CardRowDetailLine
      wrap={props.wrap}
      title={
        <HStack style={{ display: "inline-flex" }}>
          {props.label}
          {props.labelTooltip && <InfoToggletip content={props.labelTooltip} />}
        </HStack>
      }
    >
      {props.children}
    </CardRowDetailLine>
  );
};

export default BestHitCard;
export { BestHitCard };
